import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { chartApiParams, chartParam, ChartKeyDef } from "./chartKeys";
import { DAY_HINT_PERIOD, dayHintSpan, defaultForSpan, effectiveGranularity, Granularity, parseGranularity } from "./granularity";

export type ChartGranularity = {
  /** Granularidade efetiva (já com o fallback de Dia → Semana). */
  value: Granularity;
  dayDisabled: boolean;
  dayHint: string;
  set: (g: Granularity) => void;
  /** Volta ao padrão (ex.: depois de um 422 GRANULARITY_TOO_FINE). */
  reset: () => void;
  /** O que vai para a API (`g_<key>` ou `granularity`). */
  apiParams: Record<string, string>;
};

/**
 * Estado do seletor de um gráfico na URL (`g_<chartKey>`, omitido quando igual ao padrão).
 * `spanDays` = dias que o gráfico plota (período filtrado, 2 × janela, ou a série atual com `dataSpan`); null = desconhecido.
 */
export function useChartGranularity(chart: ChartKeyDef, { spanDays, dataSpan = false }: { spanDays: number | null; dataSpan?: boolean }): ChartGranularity {
  const [search, setSearch] = useSearchParams();
  const param = chartParam(chart);
  const fallback: Granularity = chart.default === "byPeriod" ? defaultForSpan(spanDays) : chart.default;
  const requested = parseGranularity(search.get(param)) ?? (chart.legacyParam ? parseGranularity(search.get(chart.legacyParam)) : null) ?? fallback;
  const { value, dayDisabled } = effectiveGranularity(requested, spanDays);
  const write = useCallback(
    (g: Granularity | null) =>
      setSearch((prev) => {
        const next = new URLSearchParams(prev);
        if (chart.legacyParam) next.delete(chart.legacyParam);
        if (g === null || g === fallback) next.delete(param);
        else next.set(param, g);
        return next;
      }),
    [setSearch, param, fallback, chart.legacyParam],
  );
  const reset = useCallback(() => write(null), [write]);
  return {
    value,
    dayDisabled,
    dayHint: dataSpan && spanDays !== null ? dayHintSpan(spanDays) : DAY_HINT_PERIOD,
    set: write,
    reset,
    apiParams: chartApiParams(chart, value),
  };
}
